import React, { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Drawer,
    Dropdown,
    Empty,
    Input,
    Modal,
    Select,
    Spin,
    Tag,
    Tooltip,
    message as antdMessage,
} from 'antd';
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CopyOutlined,
    FilterOutlined,
    MergeCellsOutlined,
    MessageOutlined,
    PlayCircleOutlined,
    ReloadOutlined,
    SearchOutlined,
    UserAddOutlined,
    UserSwitchOutlined,
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import ContentW100 from '../../Components/PageLayout/ContentW100';
import PageTitle from '../../Components/PageTitle/PageTitle';
import TaskChat from '../../Components/TaskChat/TaskChat';
import api from '../../services/api';
import { hasActiveTextSelection } from '../../utils/textSelection';
import InlineEditableField from '../../Components/InlineEditableField/InlineEditableField';
import TaskTagsEditor from '../../Components/TaskTagsEditor/TaskTagsEditor';
import styles from './TicketChatsPage.module.scss';

const STORAGE_KEY = 'it-ts-helpdesk-filters-v2';
const DEFAULT_FILTERS = {
    periodDays: 30,
    quickFilter: 'all',
    q: '',
    sort: 'sla',
    assigneeId: null,
    statusCode: null,
    categoryCode: null,
    subcategoryCode: null,
    channelCode: null,
    priority: null,
    company: '',
    terminal: '',
    slaState: null,
    includeClosed: false,
};

const quickFilters = [
    { key: 'all', label: 'Все' },
    { key: 'mine', label: 'Мои' },
    { key: 'no_staff_reply', label: 'Без ответа ТП' },
    { key: 'waiting_2h', label: 'Ждут >2ч' },
    { key: 'waiting_24h', label: 'Ждут >24ч' },
    { key: 'terminal_payment_blocked', label: 'Оплаты терминала' },
    { key: 'unassigned', label: 'Без исполнителя' },
    { key: 'inactivity', label: 'Бездействие' },
    { key: 'overdue_sla', label: 'Просрочен SLA' },
];

const periodOptions = [
    { value: 7, label: '7 дней' },
    { value: 30, label: '30 дней' },
    { value: 90, label: '90 дней' },
];

const sortOptions = [
    { value: 'sla', label: 'SLA и ожидание' },
    { value: 'waiting_desc', label: 'Дольше ждут' },
    { value: 'newest', label: 'Новые' },
    { value: 'activity_desc', label: 'Активность' },
    { value: 'priority', label: 'Приоритет' },
];

const slaOptions = [
    { value: 'normal', label: 'SLA норма' },
    { value: 'risk', label: 'SLA риск' },
    { value: 'overdue', label: 'SLA просрочен' },
    { value: 'paused', label: 'SLA пауза' },
];

function loadSavedFilters() {
    try {
        const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        return { ...DEFAULT_FILTERS, ...parsed };
    } catch {
        return DEFAULT_FILTERS;
    }
}

function compactParams(filters, page = 1, limit = 30) {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));

    Object.entries(filters).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '' || key === 'periodDays') {
            return;
        }
        if (typeof value === 'boolean') {
            if (value) params.set(key, '1');
            return;
        }
        params.set(key, String(value));
    });

    return params;
}

function formatMetric(value, suffix = '') {
    if (value === null || value === undefined || value === '' || Number.isNaN(value)) {
        return '—';
    }
    return `${value}${suffix}`;
}

function getSlaTone(state) {
    if (state === 'overdue') return 'danger';
    if (state === 'risk') return 'warning';
    if (state === 'paused') return 'pause';
    if (state === 'done') return 'done';
    return 'success';
}

function getWaitingLabel(ticket) {
    if (ticket?.waitingSide === 'staff') return 'Ожидает ТП';
    if (ticket?.waitingSide === 'client') return 'Ожидает клиента';
    return 'Без ожидания';
}

function getTicketAccent(ticket) {
    const resolutionState = ticket?.sla?.resolution?.state;
    const firstResponseState = ticket?.sla?.firstResponse?.state;
    if (ticket?.terminalPaymentBlocked) return '#be185d';
    if (resolutionState === 'overdue' || firstResponseState === 'overdue') return '#dc2626';
    if (resolutionState === 'risk' || firstResponseState === 'risk') return '#d97706';
    if (ticket?.priority?.title?.toLowerCase?.().includes('крит')) return '#b91c1c';
    if (ticket?.waitingSide === 'staff') return '#2563eb';
    return ticket?.statusColor || '#64748b';
}

function normalizeSelectOptions(items, labelKey = 'title') {
    return (Array.isArray(items) ? items : []).map((item) => ({
        value: item.value ?? item.code ?? item.id,
        label: item[labelKey] ?? item.name ?? item.title ?? item.code,
    }));
}

function ensureOption(options, value, label) {
    if (value === null || value === undefined || value === '') {
        return options;
    }

    if ((Array.isArray(options) ? options : []).some((option) => String(option.value) === String(value))) {
        return options;
    }

    return [{ value, label: label || String(value) }, ...options];
}

function formatTerminalSoftwareVersion(snapshot) {
    const version = snapshot?.version || '';
    if (!version) {
        return '—';
    }

    const parts = [version];
    if (snapshot?.terminalKey) parts.push(`терминал ${snapshot.terminalKey}`);
    if (snapshot?.checkedAt) parts.push(`проверено ${snapshot.checkedAt}`);

    return parts.join(' · ');
}

function TaskTagChips({ tags, limit = 4 }) {
    const visibleTags = Array.isArray(tags) ? tags.slice(0, limit) : [];
    if (visibleTags.length === 0) return null;

    return (
        <>
            {visibleTags.map((tag) => (
                <span
                    key={tag.id || tag.title}
                    className={styles.taskTagPill}
                    style={{ backgroundColor: tag.backgroundColor || '#e2e8f0' }}
                >
                    {tag.title}
                </span>
            ))}
            {Array.isArray(tags) && tags.length > limit ? (
                <span className={styles.smallPill}>+{tags.length - limit}</span>
            ) : null}
        </>
    );
}

function Field({ label, value, children }) {
    return (
        <div className={styles.field}>
            <span>{label}</span>
            <div className={styles.fieldValue}>{children || value || '—'}</div>
        </div>
    );
}

function SlaBadge({ label, state, leftLabel }) {
    const tone = getSlaTone(state);
    return (
        <div className={`${styles.slaBadge} ${styles[`sla_${tone}`]}`}>
            <span>{label}</span>
            <strong>{leftLabel || '—'}</strong>
        </div>
    );
}

function TaskRatingPanel({ rating, submitting, onSubmit }) {
    if (!rating?.requested) {
        return null;
    }

    const score = Number(rating.score || 0);
    const comment = typeof rating.comment === 'string' ? rating.comment.trim() : '';

    return (
        <section className={styles.ratingPanel}>
            <div className={styles.ratingText}>
                <span>Оценка закрытия</span>
                <strong>{score > 0 ? `${score}/5` : 'Ожидает оценки'}</strong>
            </div>
            {rating.canRate ? (
                <div className={styles.ratingButtons}>
                    {[1, 2, 3, 4, 5].map((value) => (
                        <button
                            key={value}
                            type="button"
                            className={`${styles.ratingButton} ${score === value ? styles.ratingButtonActive : ''}`}
                            disabled={submitting}
                            onClick={() => onSubmit(value)}
                        >
                            {value}
                        </button>
                    ))}
                </div>
            ) : (
                <span className={styles.ratingNote}>
                    {score > 0 ? 'Оценка уже поставлена' : 'Постановщик ещё не оценил закрытие'}
                </span>
            )}
            {comment ? (
                <span className={styles.ratingNote}>Комментарий: {comment}</span>
            ) : null}
        </section>
    );
}

function KpiStrip({ kpis, activeFilter, onFilter }) {
    return (
        <div className={styles.kpiStrip}>
            {(Array.isArray(kpis) ? kpis : []).map((kpi) => (
                <button
                    key={kpi.key}
                    type="button"
                    className={kpi.filter && kpi.filter === activeFilter ? styles.kpiCardActive : styles.kpiCard}
                    onClick={() => kpi.filter && onFilter(kpi.filter)}
                >
                    <span>{kpi.label}</span>
                    <strong>{formatMetric(kpi.value, kpi.suffix ? ` ${kpi.suffix}` : '')}</strong>
                </button>
            ))}
        </div>
    );
}

function QueueCard({ ticket, selected, onSelect }) {
    const resolutionState = ticket?.sla?.resolution?.state || 'normal';
    const firstResponseState = ticket?.sla?.firstResponse?.state || 'normal';
    const accent = getTicketAccent(ticket);
    const cardClassName = `${selected ? styles.queueCardActive : styles.queueCard} ${ticket?.terminalPaymentBlocked ? styles.queueCardUrgent : ''}`;

    return (
        <button
            type="button"
            className={cardClassName}
            style={{ '--ticket-accent': accent }}
            onClick={onSelect}
        >
            <div className={styles.queueTop}>
                <strong>#{ticket.id}</strong>
                <span>{ticket.dateCreate || '—'}</span>
            </div>

            <div className={styles.queueTitle}>{ticket.title || 'Без названия'}</div>

            <div className={styles.queueMeta}>
                <span>{ticket.author?.name || 'Автор не указан'}</span>
                <span>{ticket.assignee?.name || 'Без исполнителя'}</span>
            </div>

            <div className={styles.queueMeta}>
                <span>{ticket.company?.address || ticket.company?.name || 'Объект не указан'}</span>
                <span>{ticket.terminal?.number ? `Терминал ${ticket.terminal.number}` : ticket.channelTitle || 'Канал не указан'}</span>
            </div>

            <div className={styles.badgeRow}>
                <span className={styles.statusPill} style={{ '--status-color': ticket.statusColor || '#64748b' }}>
                    {ticket.statusLabel || ticket.status || 'Статус'}
                </span>
                <span className={`${styles.smallPill} ${styles[`sla_${getSlaTone(resolutionState)}`]}`}>
                    SLA {ticket.sla?.resolution?.leftLabel || '—'}
                </span>
                <span className={`${styles.smallPill} ${ticket.waitingSide === 'staff' ? styles.waitingStaff : styles.waitingClient}`}>
                    {getWaitingLabel(ticket)}
                </span>
                {ticket.terminalPaymentBlocked ? <span className={styles.terminalPaymentPill}>Терминал не принимает оплаты</span> : null}
                {firstResponseState === 'overdue' ? <span className={styles.alertPill}>1-й ответ</span> : null}
                <TaskTagChips tags={ticket.tags} limit={3} />
            </div>

            <div className={styles.lastMessage}>{ticket.lastMessagePreview || 'Сообщений пока нет'}</div>
        </button>
    );
}

export default function TicketChatsPage() {
    const [messageApi, contextHolder] = antdMessage.useMessage();
    const [searchParams, setSearchParams] = useSearchParams();
    const [filters, setFilters] = useState(loadSavedFilters);
    const [overview, setOverview] = useState({ kpis: [], summary: {}, assignees: [] });
    const [catalogs, setCatalogs] = useState({ statuses: [], categories: [], subcategories: [], channels: [] });
    const [taskUsers, setTaskUsers] = useState({ authors: [], performers: [], observers: [] });
    const [tickets, setTickets] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: 30, total: 0, hasMore: false });
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [ratingSubmitting, setRatingSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [actionModal, setActionModal] = useState(null);
    const [actionDraft, setActionDraft] = useState({});
    const selectedTaskId = Number(searchParams.get('taskId') || 0);
    const refreshTimerRef = useRef(null);
    const wsRef = useRef(null);

    const setFilterValue = useCallback((key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    }, []);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    }, [filters]);

    const fetchCatalogs = useCallback(async () => {
        const response = await api.get('/api/it-ts/catalogs');
        setCatalogs(response.data || {});
    }, []);

    const fetchTaskUsers = useCallback(async () => {
        try {
            const response = await api.get('/api/usersTask?teamCode=TS');
            setTaskUsers({
                authors: normalizeSelectOptions(response.data?.Authors || [], 'label'),
                performers: normalizeSelectOptions(response.data?.Performers || [], 'label'),
                observers: normalizeSelectOptions(response.data?.Observer || [], 'label'),
            });
        } catch (error) {
            console.error('Ошибка загрузки пользователей для тикетов:', error);
        }
    }, []);

    const fetchOverview = useCallback(async () => {
        const params = new URLSearchParams();
        params.set('periodDays', String(filters.periodDays || 30));
        const response = await api.get(`/api/it-ts/overview?${params.toString()}`);
        setOverview(response.data || { kpis: [], summary: {}, assignees: [] });
    }, [filters.periodDays]);

    const fetchTickets = useCallback(async ({ page = 1, append = false, silent = false } = {}) => {
        if (!silent) setLoading(true);
        try {
            const response = await api.get(`/api/it-ts/tickets?${compactParams(filters, page, pagination.limit).toString()}`);
            const nextItems = Array.isArray(response.data?.items) ? response.data.items : [];
            setTickets((prev) => (append ? [...prev, ...nextItems] : nextItems));
            setPagination({
                page: Number(response.data?.page || page),
                limit: Number(response.data?.limit || pagination.limit),
                total: Number(response.data?.total || 0),
                hasMore: Boolean(response.data?.hasMore),
            });
            setErrorMessage('');
        } catch (error) {
            console.error('Ошибка загрузки очереди IT-ТП:', error);
            setErrorMessage(error?.response?.status === 403
                ? 'Нет доступа к helpdesk IT-ТП для текущей роли.'
                : 'Не удалось загрузить очередь тикетов.');
            if (!append) setTickets([]);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [filters, pagination.limit]);

    const fetchDetail = useCallback(async (taskId, { silent = false } = {}) => {
        if (!taskId) {
            setDetail(null);
            return;
        }
        if (!silent) setDetailLoading(true);
        try {
            const response = await api.get(`/api/it-ts/tickets/${taskId}`);
            setDetail(response.data || null);
        } catch (error) {
            console.error('Ошибка загрузки тикета IT-ТП:', error);
            setDetail(null);
        } finally {
            if (!silent) setDetailLoading(false);
        }
    }, []);

    const refreshAll = useCallback(async ({ silent = false } = {}) => {
        await Promise.all([
            fetchOverview(),
            fetchTickets({ page: 1, append: false, silent }),
            selectedTaskId ? fetchDetail(selectedTaskId, { silent: true }) : Promise.resolve(),
        ]);
    }, [fetchOverview, fetchTickets, fetchDetail, selectedTaskId]);

    useEffect(() => {
        void fetchCatalogs();
        void fetchTaskUsers();
    }, [fetchCatalogs, fetchTaskUsers]);

    useEffect(() => {
        const timerId = setTimeout(() => {
            void refreshAll();
        }, 180);

        return () => clearTimeout(timerId);
    }, [refreshAll]);

    useEffect(() => {
        if (!selectedTaskId && tickets.length > 0) {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set('taskId', String(tickets[0].id));
                return next;
            }, { replace: true });
            return;
        }

        if (selectedTaskId) {
            void fetchDetail(selectedTaskId);
        }
    }, [selectedTaskId, tickets, fetchDetail, setSearchParams]);

    useEffect(() => {
        const wsUrl = process.env.REACT_APP_SUPPORT_WS_URL;
        if (!wsUrl) return undefined;

        let closed = false;

        const connect = async () => {
            try {
                const response = await api.post('/api/it-ts/ws-token');
                const token = response.data?.token;
                if (!token || closed) return;

                const separator = wsUrl.includes('?') ? '&' : '?';
                const socket = new WebSocket(`${wsUrl}${separator}token=${encodeURIComponent(token)}`);
                wsRef.current = socket;
                socket.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        if (!data?.type || data.type === 'connected') return;
                        window.clearTimeout(refreshTimerRef.current);
                        refreshTimerRef.current = window.setTimeout(() => {
                            void refreshAll({ silent: true });
                        }, data.type === 'message.created' ? 120 : 260);
                    } catch {
                        // Ignore malformed dev websocket payloads.
                    }
                };
                socket.onclose = () => {
                    if (!closed) {
                        window.setTimeout(connect, 3000);
                    }
                };
            } catch {
                if (!closed) {
                    window.setTimeout(connect, 5000);
                }
            }
        };

        void connect();

        return () => {
            closed = true;
            window.clearTimeout(refreshTimerRef.current);
            wsRef.current?.close?.();
        };
    }, [refreshAll]);

    useEffect(() => {
        const onKeyDown = (event) => {
            const tagName = String(event.target?.tagName || '').toLowerCase();
            if (['input', 'textarea', 'select'].includes(tagName) || event.target?.isContentEditable) return;
            if (event.ctrlKey || event.metaKey || event.altKey || hasActiveTextSelection()) return;
            if (event.key === 'r') {
                event.preventDefault();
                void refreshAll();
            }
            if ((event.key === 'j' || event.key === 'k') && tickets.length > 0) {
                event.preventDefault();
                const currentIndex = tickets.findIndex((ticket) => Number(ticket.id) === selectedTaskId);
                const nextIndex = event.key === 'j'
                    ? Math.min(tickets.length - 1, currentIndex + 1)
                    : Math.max(0, currentIndex - 1);
                const nextTicket = tickets[nextIndex >= 0 ? nextIndex : 0];
                if (nextTicket) selectTicket(nextTicket.id);
            }
            if (event.key === 'a' && selectedTaskId) {
                event.preventDefault();
                void runAction('take');
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [refreshAll, tickets, selectedTaskId]);

    const selectTicket = useCallback((taskId) => {
        startTransition(() => {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set('taskId', String(taskId));
                return next;
            }, { replace: true });
        });
    }, [setSearchParams]);

    const selectedTicket = detail?.ticket || tickets.find((ticket) => Number(ticket.id) === selectedTaskId) || null;

    const filteredSubcategories = useMemo(() => {
        const categoryCode = filters.categoryCode;
        return (catalogs.subcategories || []).filter((item) => !categoryCode || item.parentCode === categoryCode);
    }, [catalogs.subcategories, filters.categoryCode]);

    const priorityOptions = useMemo(() => {
        const values = new Map();
        tickets.forEach((ticket) => {
            const id = ticket.priority?.id;
            const title = ticket.priority?.title;
            if (title) values.set(String(id || title), { value: id || title, label: title });
        });
        if (selectedTicket?.priority?.title) {
            const id = selectedTicket.priority.id;
            const title = selectedTicket.priority.title;
            values.set(String(id || title), { value: id || title, label: title });
        }
        return Array.from(values.values());
    }, [selectedTicket, tickets]);

    const assigneeOptions = useMemo(() => {
        const values = new Map();
        (Array.isArray(taskUsers.performers) ? taskUsers.performers : []).forEach((item) => {
            values.set(String(item.value), item);
        });
        (Array.isArray(overview.assignees) ? overview.assignees : []).forEach((item) => {
            if (item?.id) values.set(String(item.id), { value: item.id, label: item.name });
        });
        if (selectedTicket?.assignee?.id) {
            values.set(String(selectedTicket.assignee.id), {
                value: selectedTicket.assignee.id,
                label: selectedTicket.assignee.name,
            });
        }

        return Array.from(values.values());
    }, [overview.assignees, selectedTicket, taskUsers.performers]);

    const authorOptions = useMemo(
        () => ensureOption(taskUsers.authors, selectedTicket?.author?.id, selectedTicket?.author?.name),
        [selectedTicket, taskUsers.authors]
    );

    const observerOptions = useMemo(
        () => (Array.isArray(taskUsers.observers) ? taskUsers.observers : []),
        [taskUsers.observers]
    );

    const statusOptions = useMemo(
        () => (catalogs.statuses || []).map((status) => ({ value: status.code, label: status.label })),
        [catalogs.statuses]
    );

    const observerIds = useMemo(
        () => (Array.isArray(detail?.observers) ? detail.observers.map((observer) => observer.id) : []),
        [detail?.observers]
    );

    const observerLabel = useMemo(
        () => (Array.isArray(detail?.observers)
            ? detail.observers.map((observer) => observer.name).filter(Boolean).join(', ')
            : ''),
        [detail?.observers]
    );

    const terminalSoftwareVersion = formatTerminalSoftwareVersion(selectedTicket?.terminalSoftwareVersion);

    const runAction = useCallback(async (action, payload = {}) => {
        if (!selectedTaskId) return;
        try {
            const response = await api.post(`/api/it-ts/tickets/${selectedTaskId}/actions`, { action, ...payload });
            setActionModal(null);
            setActionDraft({});
            if (response.data?.createdTicketId) {
                selectTicket(response.data.createdTicketId);
            }
            await refreshAll({ silent: true });
            messageApi.success('Тикет обновлён');
        } catch (error) {
            console.error('Ошибка действия с тикетом:', error);
            messageApi.error('Не удалось выполнить действие');
        }
    }, [messageApi, refreshAll, selectTicket, selectedTaskId]);

    const patchTicket = useCallback(async (payload = {}) => {
        if (!selectedTaskId) return;
        try {
            await api.patch(`/tasks/${selectedTaskId}`, payload);
            await refreshAll({ silent: true });
            messageApi.success('Тикет обновлён');
        } catch (error) {
            console.error('Ошибка inline-обновления тикета:', error);
            messageApi.error('Не удалось обновить тикет');
        }
    }, [messageApi, refreshAll, selectedTaskId]);

    const submitRating = useCallback(async (score) => {
        if (!selectedTaskId) return;
        try {
            setRatingSubmitting(true);
            const response = await api.post(`/api/tasks/${selectedTaskId}/rating`, { score });
            const nextRating = response.data?.rating;
            if (nextRating) {
                setDetail((current) => current?.ticket
                    ? { ...current, ticket: { ...current.ticket, rating: nextRating } }
                    : current);
                setTickets((current) => current.map((ticket) => (
                    Number(ticket.id) === selectedTaskId ? { ...ticket, rating: nextRating } : ticket
                )));
            }
            await refreshAll({ silent: true });
            messageApi.success('Оценка сохранена');
        } catch (error) {
            console.error('Ошибка сохранения оценки тикета:', error);
            messageApi.error('Не удалось сохранить оценку');
        } finally {
            setRatingSubmitting(false);
        }
    }, [messageApi, refreshAll, selectedTaskId]);

    const openActionModal = (type, initial = {}) => {
        setActionModal(type);
        setActionDraft(initial);
    };

    const submitModalAction = () => {
        if (actionModal === 'assign') {
            void runAction('assign', { assigneeId: actionDraft.assigneeId || null });
            return;
        }
        if (actionModal === 'observer_add') {
            void runAction('observer_add', { observerId: actionDraft.observerId });
            return;
        }
        if (actionModal === 'internal_comment') {
            void runAction('internal_comment', { text: actionDraft.text || '' });
            return;
        }
        if (actionModal === 'request_client_data') {
            void runAction('request_client_data', { text: actionDraft.text || '' });
            return;
        }
        if (actionModal === 'duplicate') {
            void runAction('duplicate', { title: actionDraft.title, note: actionDraft.note });
            return;
        }
        if (actionModal === 'merge') {
            void runAction('merge', { targetTaskId: actionDraft.targetTaskId, note: actionDraft.note });
        }
    };

    const statusMenu = {
        items: (catalogs.statuses || []).map((status) => ({
            key: status.code,
            label: status.label,
        })),
        onClick: ({ key }) => runAction('status', { statusCode: key }),
    };

    const resetFilters = () => {
        setFilters(DEFAULT_FILTERS);
    };

    const renderActionModal = () => {
        const titleMap = {
            assign: 'Назначить исполнителя',
            observer_add: 'Добавить наблюдателя',
            internal_comment: 'Внутренний комментарий',
            request_client_data: 'Запросить данные у клиента',
            duplicate: 'Дублировать тикет',
            merge: 'Объединить тикеты',
        };

        return (
            <Modal
                open={Boolean(actionModal)}
                title={actionModal ? titleMap[actionModal] : ''}
                onCancel={() => {
                    setActionModal(null);
                    setActionDraft({});
                }}
                onOk={submitModalAction}
                okText="Выполнить"
                cancelText="Отмена"
                destroyOnClose
            >
                {actionModal === 'assign' ? (
                    <Select
                        value={actionDraft.assigneeId}
                        onChange={(value) => setActionDraft((prev) => ({ ...prev, assigneeId: value }))}
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        options={assigneeOptions}
                        placeholder="Исполнитель"
                        className={styles.modalControl}
                    />
                ) : null}

                {actionModal === 'observer_add' ? (
                    <Select
                        value={actionDraft.observerId}
                        onChange={(value) => setActionDraft((prev) => ({ ...prev, observerId: value }))}
                        showSearch
                        optionFilterProp="label"
                        options={assigneeOptions}
                        placeholder="Наблюдатель"
                        className={styles.modalControl}
                    />
                ) : null}

                {['internal_comment', 'request_client_data'].includes(actionModal) ? (
                    <Input.TextArea
                        value={actionDraft.text}
                        onChange={(event) => setActionDraft((prev) => ({ ...prev, text: event.target.value }))}
                        rows={5}
                        placeholder="Текст сообщения"
                        className={styles.modalControl}
                    />
                ) : null}

                {actionModal === 'duplicate' ? (
                    <div className={styles.modalStack}>
                        <Input
                            value={actionDraft.title}
                            onChange={(event) => setActionDraft((prev) => ({ ...prev, title: event.target.value }))}
                            placeholder="Название нового тикета"
                        />
                        <Input.TextArea
                            value={actionDraft.note}
                            onChange={(event) => setActionDraft((prev) => ({ ...prev, note: event.target.value }))}
                            rows={3}
                            placeholder="Комментарий"
                        />
                    </div>
                ) : null}

                {actionModal === 'merge' ? (
                    <div className={styles.modalStack}>
                        <Input
                            value={actionDraft.targetTaskId}
                            onChange={(event) => setActionDraft((prev) => ({ ...prev, targetTaskId: event.target.value }))}
                            placeholder="ID тикета, куда объединить"
                        />
                        <Input.TextArea
                            value={actionDraft.note}
                            onChange={(event) => setActionDraft((prev) => ({ ...prev, note: event.target.value }))}
                            rows={3}
                            placeholder="Комментарий"
                        />
                    </div>
                ) : null}
            </Modal>
        );
    };

    return (
        <ContentW100>
            {contextHolder}
            <PageTitle title="Чаты тикетов" />

            <div className={styles.page}>
                {errorMessage ? <Alert type="error" showIcon message={errorMessage} /> : null}

                <div className={styles.topBar}>
                    <div className={styles.searchWrap}>
                        <SearchOutlined />
                        <Input
                            value={filters.q}
                            onChange={(event) => setFilterValue('q', event.target.value)}
                            placeholder="Номер, тема, автор, компания, терминал, текст переписки"
                            bordered={false}
                            allowClear
                        />
                    </div>

                    <Select
                        value={filters.sort}
                        onChange={(value) => setFilterValue('sort', value)}
                        options={sortOptions}
                        className={styles.sortSelect}
                    />

                    <Button icon={<FilterOutlined />} onClick={() => setDrawerOpen(true)}>
                        Фильтры
                    </Button>
                    <Tooltip title="Обновить">
                        <Button icon={<ReloadOutlined />} onClick={() => refreshAll()} />
                    </Tooltip>
                </div>

                <KpiStrip
                    kpis={overview.kpis}
                    activeFilter={filters.quickFilter}
                    onFilter={(nextFilter) => setFilterValue('quickFilter', nextFilter)}
                />

                <div className={styles.quickFilters}>
                    {quickFilters.map((filter) => (
                        <button
                            key={filter.key}
                            type="button"
                            className={filters.quickFilter === filter.key ? styles.quickFilterActive : styles.quickFilter}
                            onClick={() => setFilterValue('quickFilter', filter.key)}
                        >
                            {filter.label}
                        </button>
                    ))}
                </div>

                <div className={styles.workspace}>
                    <aside className={styles.queuePane}>
                        <div className={styles.paneHeader}>
                            <div>
                                <span>Очередь</span>
                                <strong>{pagination.total}</strong>
                            </div>
                            <Tag>{tickets.length}</Tag>
                        </div>

                        <Spin spinning={loading}>
                            <div className={styles.queueList}>
                                {tickets.length === 0 ? (
                                    <Empty description="Тикеты не найдены" />
                                ) : tickets.map((ticket) => (
                                    <QueueCard
                                        key={ticket.id}
                                        ticket={ticket}
                                        selected={Number(ticket.id) === selectedTaskId}
                                        onSelect={() => selectTicket(ticket.id)}
                                    />
                                ))}
                            </div>
                        </Spin>

                        {pagination.hasMore ? (
                            <Button
                                block
                                onClick={() => fetchTickets({ page: pagination.page + 1, append: true, silent: true })}
                            >
                                Загрузить ещё
                            </Button>
                        ) : null}
                    </aside>

                    <main className={styles.ticketPane}>
                        <Spin spinning={detailLoading}>
                            {selectedTicket ? (
                                <>
                                    <section className={styles.ticketHeader}>
                                        <div>
                                            <div className={styles.ticketEyebrow}>#{selectedTicket.id}</div>
                                            <h1>
                                                <InlineEditableField
                                                    value={selectedTicket.title || ''}
                                                    type="textarea"
                                                    rows={2}
                                                    onSave={(value) => patchTicket({ title: String(value).trim() })}
                                                    viewClassName={styles.ticketTitleInline}
                                                />
                                            </h1>
                                            <div className={styles.ticketTags}>
                                                <span className={styles.statusPill} style={{ '--status-color': selectedTicket.statusColor || '#64748b' }}>
                                                    {selectedTicket.statusLabel || selectedTicket.status}
                                                </span>
                                                {selectedTicket.terminalPaymentBlocked ? <span className={styles.terminalPaymentPill}>Терминал не принимает оплаты</span> : null}
                                                <Tag>{selectedTicket.priority?.title || 'Без приоритета'}</Tag>
                                                {selectedTicket.categoryTitle ? <Tag>{selectedTicket.categoryTitle}</Tag> : null}
                                                <TaskTagsEditor
                                                    value={selectedTicket.tags}
                                                    onSave={(tags) => patchTicket({ tags })}
                                                    className={styles.ticketTagEditor}
                                                />
                                            </div>
                                        </div>

                                        <div className={styles.actionBar}>
                                            <Tooltip title="Взять в работу">
                                                <Button icon={<PlayCircleOutlined />} onClick={() => runAction('take')} />
                                            </Tooltip>
                                            <Tooltip title="Назначить">
                                                <Button icon={<UserSwitchOutlined />} onClick={() => openActionModal('assign', { assigneeId: selectedTicket.assignee?.id })} />
                                            </Tooltip>
                                            <Dropdown menu={statusMenu} trigger={['click']}>
                                                <Button icon={<CheckCircleOutlined />}>Статус</Button>
                                            </Dropdown>
                                            <Tooltip title="Добавить наблюдателя">
                                                <Button icon={<UserAddOutlined />} onClick={() => openActionModal('observer_add')} />
                                            </Tooltip>
                                            <Tooltip title="Внутренний комментарий">
                                                <Button icon={<MessageOutlined />} onClick={() => openActionModal('internal_comment')} />
                                            </Tooltip>
                                        </div>
                                    </section>

                                    <section className={styles.slaRow}>
                                        <SlaBadge
                                            label="Первый ответ"
                                            state={selectedTicket.sla?.firstResponse?.state}
                                            leftLabel={selectedTicket.sla?.firstResponse?.leftLabel}
                                        />
                                        <SlaBadge
                                            label="Решение"
                                            state={selectedTicket.sla?.resolution?.state}
                                            leftLabel={selectedTicket.sla?.resolution?.leftLabel}
                                        />
                                        <div className={styles.waitingBox}>
                                            <ClockCircleOutlined />
                                            <div>
                                                <span>{getWaitingLabel(selectedTicket)}</span>
                                                <strong>{selectedTicket.waitingLabel || '—'}</strong>
                                            </div>
                                        </div>
                                    </section>

                                    <TaskRatingPanel
                                        rating={selectedTicket.rating}
                                        submitting={ratingSubmitting}
                                        onSubmit={submitRating}
                                    />

                                    <section className={styles.fieldGrid}>
                                        <Field label="Автор">
                                            <InlineEditableField
                                                value={selectedTicket.author?.id || ''}
                                                displayValue={selectedTicket.author?.name || '—'}
                                                type="select"
                                                options={authorOptions}
                                                onSave={(value) => patchTicket({ author: value })}
                                            />
                                        </Field>
                                        <Field label="Менеджер" value={selectedTicket.manager?.name} />
                                        <Field label="Исполнитель">
                                            <InlineEditableField
                                                value={selectedTicket.assignee?.id || ''}
                                                displayValue={selectedTicket.assignee?.name || '—'}
                                                type="select"
                                                options={assigneeOptions}
                                                onSave={(value) => patchTicket({ assigneeId: value || null })}
                                            />
                                        </Field>
                                        <Field label="Команда" value={selectedTicket.team?.title} />
                                        <Field label="Статус">
                                            <InlineEditableField
                                                value={selectedTicket.statusCode || ''}
                                                displayValue={selectedTicket.statusLabel || selectedTicket.status || '—'}
                                                type="select"
                                                options={statusOptions}
                                                onSave={(value) => patchTicket({ statusCode: value })}
                                            />
                                        </Field>
                                        <Field label="Важность">
                                            <InlineEditableField
                                                value={selectedTicket.priority?.id || selectedTicket.priority?.title || ''}
                                                displayValue={selectedTicket.priority?.title || '—'}
                                                type="select"
                                                options={priorityOptions}
                                                onSave={(value) => patchTicket({ priority: value })}
                                            />
                                        </Field>
                                        <Field label="Тип заявки" value={selectedTicket.channelTitle} />
                                        <Field label="Категория" value={selectedTicket.categoryTitle} />
                                        <Field label="Подкатегория" value={selectedTicket.subcategoryTitle} />
                                        <Field label="Терминал">
                                            <InlineEditableField
                                                value={selectedTicket.terminal?.number || ''}
                                                onSave={(value) => patchTicket({ terminalNumber: value })}
                                            />
                                        </Field>
                                        <Field label="Версия ПО" value={terminalSoftwareVersion} />
                                        <Field label="Телефон для связи">
                                            <InlineEditableField
                                                value={selectedTicket.contactPhone || ''}
                                                onSave={(value) => patchTicket({ contactPhone: value })}
                                            />
                                        </Field>
                                        <Field label="Компания" value={selectedTicket.company?.name} />
                                        <Field label="Объект" value={selectedTicket.company?.address} />
                                        <Field label="Дата создания" value={selectedTicket.dateCreate} />
                                        <Field label="Срок решения">
                                            <InlineEditableField
                                                value={selectedTicket.estimatedResolutionDate || ''}
                                                displayValue={selectedTicket.estimatedResolutionLabel || selectedTicket.sla?.resolution?.dueAt || '—'}
                                                type="date"
                                                onSave={(value) => patchTicket({ estimatedResolutionDate: value || null })}
                                            />
                                        </Field>
                                        <Field label="Наблюдатели">
                                            <InlineEditableField
                                                value={observerIds}
                                                displayValue={observerLabel || '—'}
                                                type="multiselect"
                                                options={observerOptions}
                                                onSave={(value) => patchTicket({ observers: Array.isArray(value) ? value : [] })}
                                            />
                                        </Field>
                                        <Field label="Оплаты терминала">
                                            <InlineEditableField
                                                value={Boolean(selectedTicket.terminalPaymentBlocked)}
                                                type="checkbox"
                                                onSave={(value) => patchTicket({ terminalPaymentBlocked: Boolean(value) })}
                                            />
                                        </Field>
                                    </section>

                                    <section className={styles.descriptionBlock}>
                                        <div className={styles.sectionTitle}>Описание задачи</div>
                                        <InlineEditableField
                                            value={selectedTicket.description || ''}
                                            type="textarea"
                                            rows={5}
                                            placeholder="Описание не заполнено."
                                            onSave={(value) => patchTicket({ description: value })}
                                        />
                                    </section>

                                    <section className={styles.attachmentsBlock}>
                                        <div className={styles.sectionTitle}>Вложения</div>
                                        {(detail?.messages || []).some((message) => Array.isArray(message.files) && message.files.length > 0) ? (
                                            <div className={styles.attachmentsList}>
                                                {detail.messages.flatMap((message) => (message.files || []).map((file, index) => {
                                                    const link = typeof file === 'string' ? file : (file?.link || file?.url || '');
                                                    if (!link) return null;
                                                    return (
                                                        <a key={`${message.id}-${index}`} href={link} target="_blank" rel="noreferrer">
                                                            {link}
                                                        </a>
                                                    );
                                                }))}
                                            </div>
                                        ) : (
                                            <p>Файлы пока не прикреплены.</p>
                                        )}
                                    </section>

                                    <section className={styles.historyBlock}>
                                        <div className={styles.sectionTitle}>История изменений</div>
                                        {Array.isArray(detail?.activity) && detail.activity.length > 0 ? (
                                            <div className={styles.historyList}>
                                                {detail.activity.slice(0, 8).map((item) => (
                                                    <div key={item.id} className={styles.historyItem}>
                                                        <strong>{item.actorName || 'Система'}</strong>
                                                        <span>{item.actionLabel || item.actionType}</span>
                                                        <em>{item.createdAt || '—'}</em>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p>История пока пуста.</p>
                                        )}
                                    </section>

                                    <section className={styles.bottomActions}>
                                        <Button onClick={() => openActionModal('request_client_data')}>Запросить данные</Button>
                                        <Button icon={<CopyOutlined />} onClick={() => openActionModal('duplicate', { title: `Дубликат: ${selectedTicket.title || ''}` })}>Дублировать</Button>
                                        <Button icon={<MergeCellsOutlined />} onClick={() => openActionModal('merge')}>Объединить</Button>
                                        {selectedTicket.isClosed ? (
                                            <Button onClick={() => runAction('reopen')}>Переоткрыть</Button>
                                        ) : (
                                            <Button danger onClick={() => runAction('close')}>Закрыть</Button>
                                        )}
                                    </section>
                                </>
                            ) : (
                                <Empty description="Выберите тикет в очереди" />
                            )}
                        </Spin>
                    </main>

                    <aside className={styles.chatPane}>
                        <div className={styles.paneHeader}>
                            <div>
                                <span>Диалог</span>
                                <strong>{selectedTicket ? `#${selectedTicket.id}` : '—'}</strong>
                            </div>
                            {selectedTicket?.waitingSide ? <Tag>{getWaitingLabel(selectedTicket)}</Tag> : null}
                        </div>
                        {selectedTaskId ? (
                            <TaskChat
                                key={selectedTaskId}
                                taskId={selectedTaskId}
                                templatesResponse={[]}
                                mentionCandidates={assigneeOptions.map((item) => ({ id: item.value, label: item.label }))}
                                onMessagesChanged={() => refreshAll({ silent: true })}
                            />
                        ) : (
                            <Empty description="Нет выбранного тикета" />
                        )}
                    </aside>
                </div>
            </div>

            <Drawer
                title="Фильтры"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                width={420}
                extra={<Button onClick={resetFilters}>Сбросить</Button>}
            >
                <div className={styles.drawerStack}>
                    <label>
                        Период аналитики
                        <Select
                            value={filters.periodDays}
                            onChange={(value) => setFilterValue('periodDays', value)}
                            options={periodOptions}
                        />
                    </label>
                    <label>
                        Исполнитель
                        <Select
                            value={filters.assigneeId}
                            onChange={(value) => setFilterValue('assigneeId', value)}
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            options={assigneeOptions}
                        />
                    </label>
                    <label>
                        Статус
                        <Select
                            value={filters.statusCode}
                            onChange={(value) => setFilterValue('statusCode', value)}
                            allowClear
                            options={normalizeSelectOptions(catalogs.statuses, 'label')}
                        />
                    </label>
                    <label>
                        Категория
                        <Select
                            value={filters.categoryCode}
                            onChange={(value) => {
                                setFilters((prev) => ({ ...prev, categoryCode: value, subcategoryCode: null }));
                            }}
                            allowClear
                            options={normalizeSelectOptions(catalogs.categories)}
                        />
                    </label>
                    <label>
                        Подкатегория
                        <Select
                            value={filters.subcategoryCode}
                            onChange={(value) => setFilterValue('subcategoryCode', value)}
                            allowClear
                            options={normalizeSelectOptions(filteredSubcategories)}
                        />
                    </label>
                    <label>
                        Приоритет
                        <Select
                            value={filters.priority}
                            onChange={(value) => setFilterValue('priority', value)}
                            allowClear
                            options={priorityOptions}
                        />
                    </label>
                    <label>
                        Компания
                        <Input
                            value={filters.company}
                            onChange={(event) => setFilterValue('company', event.target.value)}
                            allowClear
                        />
                    </label>
                    <label>
                        Терминал
                        <Input
                            value={filters.terminal}
                            onChange={(event) => setFilterValue('terminal', event.target.value)}
                            allowClear
                        />
                    </label>
                    <label>
                        SLA
                        <Select
                            value={filters.slaState}
                            onChange={(value) => setFilterValue('slaState', value)}
                            allowClear
                            options={slaOptions}
                        />
                    </label>
                    <label>
                        Канал обращения
                        <Select
                            value={filters.channelCode}
                            onChange={(value) => setFilterValue('channelCode', value)}
                            allowClear
                            options={normalizeSelectOptions(catalogs.channels)}
                        />
                    </label>
                    <label className={styles.checkboxRow}>
                        <input
                            type="checkbox"
                            checked={Boolean(filters.includeClosed)}
                            onChange={(event) => setFilterValue('includeClosed', event.target.checked)}
                        />
                        <span>Показывать закрытые</span>
                    </label>
                </div>
            </Drawer>

            {renderActionModal()}
        </ContentW100>
    );
}
